import React from "react";
import ReactMarkdown from "react-markdown";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { BlockEntrySection } from "./BlockEntries";

// Same component map the Operations Assistant panel uses, so agent output
// and Rovis read alike: an agent summary is the same kind of generated
// prose, and rendering it as one flat paragraph made a five-violation
// breakdown much harder to scan than the same content in chat.
const markdownComponents = {
  p: ({ node, ...props }) => (
    <p className="text-sm text-slate-700 leading-relaxed mt-2" {...props} />
  ),
  strong: ({ node, ...props }) => (
    <strong className="font-semibold text-slate-900" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="list-disc ml-5 mt-2 text-sm text-slate-700" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal ml-5 mt-2 text-sm text-slate-700" {...props} />
  ),
  li: ({ node, ...props }) => (
    <li className="mt-1 text-sm text-slate-700 leading-relaxed" {...props} />
  ),
};

// AI agent output block. The stored run for the viewed date renders here;
// runs happen automatically when the morning email is built, and managers can
// regenerate on demand. Run data + regenerate wiring land with the agents
// phase; until then a block without a run shows the quiet placeholder.
function AgentBlock(props) {
  const run = props.run;

  return (
    <div
      className="group w-full border border-violet-200 rounded-xl px-4 py-3.5"
      style={{ background: "linear-gradient(180deg, #f8f6ff 0%, #ffffff 70%)" }}
    >
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-lg bg-violet-700 flex items-center justify-center shrink-0">
          <AutoAwesomeOutlinedIcon style={{ fontSize: "13px", color: "#ffffff" }} />
        </div>
        <p className="text-sm font-semibold text-slate-900 flex-1 truncate">
          {props.agentName || "AI Agent"}
        </p>
        {run?.ran_at_formatted && (
          <p className="text-[11px] text-slate-400">{run.ran_at_formatted}</p>
        )}
        {props.isRegenerating || run?.status === "running" ? (
          // A run is underway - client-local, or server-side (the
          // morning-email pipeline, another device's regenerate; the
          // payload then reports status "running"). Offering Regenerate
          // would force a second LLM run racing the first.
          <p className="text-[11px] font-bold text-slate-400">
            Regenerating...
          </p>
        ) : props.onRegenerate ? (
          <button
            className="text-[11px] font-bold text-violet-700 hover:text-violet-900"
            onClick={props.onRegenerate}
          >
            ↻ Regenerate
          </button>
        ) : null}
      </div>

      {run?.output_text ? (
        <div className="mt-1">
          <ReactMarkdown components={markdownComponents}>
            {run.output_text}
          </ReactMarkdown>
        </div>
      ) : (
        <p className="text-xs text-slate-400 mt-2">
          The summary for this day will appear here after the agent runs with
          the morning email.
        </p>
      )}

      <BlockEntrySection
        blockUuid={props.block.uuid}
        entries={props.entries}
        responseOutstanding={false}
        currentUser={props.currentUser}
        post_logbook_block_entry={props.post_logbook_block_entry}
        delete_logbook_block_entry={props.delete_logbook_block_entry}
      />
    </div>
  );
}

export default AgentBlock;
